/**

 * 对接人（代理录入）会话与跳转

 * Magic Link：/login?token= → preview → login/register/scan(+token) → proxyInvite → ACL

 *

 * 定稿：ACL 非独立工作台，仅个人模式附加数据源（GET /user · grants 字段）

 */

import { setCompanyWorkspace, clearWorkspaceScope } from '@/constants/workspaceScope'

import { PROXY_DEFAULT_HOME } from '@/constants/proxyRoles'

import { RBAC } from '@/constants/rbacPermissions'

import { getSessionPermissionNamesFromStorage, hasAnyPermission } from '@/utils/rbac'

import {

  isProxyEntryBlocked,

  isProxyAccessAuthorized,

  isProxyInviteActivated,

  normalizeProxyInvite,

  pickGrantAuthFlags

} from '@/utils/proxyAccess'

import {
  getRegistrationFormGrantsFromUser,
  resolveRegistrationFormEditorPermissionNames
} from '@/utils/registrationFormGrants'

import { clearProxyInviteToken } from '@/utils/proxyInviteToken'
import { markInviteOrCollaboratorLinkEntry } from '@/utils/verificationLoginPrompt'

/** @deprecated 定稿后不再写入 localStorage；保留 key 供登出清理旧数据 */

export const ACL_GRANT_COMPANY_KEY = 'aclGrantCompanyId'



/** @deprecated ACL 时代请用 isProxyAccessAuthorized / hasProxyRegistrationPermission */

export function isProxyUser () {

  return false

}



/** 读取 localStorage 中的 user */

export function getStoredUser () {

  try {

    const raw = localStorage.getItem('user')

    return raw ? JSON.parse(raw) : null

  } catch {

    return null

  }

}



/** 照片直播协作者默认进入照片直播列表。 */
export function hasAlbumCollaboration (user = getStoredUser()) {
  const count = Number(
    user?.collaboratingAlbumsCount
    ?? user?.collaborating_albums_count
    ?? 0
  )
  return Number.isFinite(count) && count > 0
}
/** 当前会话是否具备代理录入相关 RBAC 叶子（个人模式下含 ACL grant） */

export function hasProxyRegistrationPermission (permissionNames) {

  const names = permissionNames ?? getSessionPermissionNamesFromStorage()

  return hasAnyPermission(names, [

    RBAC.PROXY_ENTRY,

    RBAC.PROXY_SUBMISSION_LIST,

    RBAC.PROXY_SUBMISSION_EDIT,

    RBAC.PROXY_MEETING_LIST,

    RBAC.REGISTRATION_SUBMISSION_LIST,

    RBAC.REGISTRATION_SUBMISSION_EDIT,

    RBAC.ORDER_LIST

  ])

}



/** 是否具备表单设计/设置能力（编辑报名表单） */

export function canEditRegistrationForm (permissionNames) {

  const names = permissionNames ?? getSessionPermissionNamesFromStorage()

  return hasAnyPermission(names, RBAC.REGISTRATION_EDIT)

}



/**

 * 对接人精简工作台：有代填/订单等权限，但无「编辑报名表单」

 * @param {string[]|undefined} permissionNames 已合并 user ∪ form.effectivePermissions

 */

export function isProxyOnlyWorkbench (permissionNames, options = {}) {

  const user = options.user ?? getStoredUser()

  const names = permissionNames ?? getSessionPermissionNamesFromStorage()

  const formId = options.formId != null ? String(options.formId).trim() : ''

  const grantScoped = formId
    ? resolveRegistrationFormEditorPermissionNames(user, formId, [])
    : null

  const editCheckNames = grantScoped ?? names

  if (canEditRegistrationForm(editCheckNames)) return false

  return hasProxyRegistrationPermission(grantScoped ?? names)

}



/**
 * @deprecated ACL 与自建表单统一 DefaultSideNav，MainLayout 已不再调用。
 * 是否应使用对接人侧栏（表单编辑页 + 对接人精简工作台）
 */
export function shouldUseProxySideNav (route, permissionNames) {
  const onFormEdit = route?.name === 'RegistrationFormEdit'
    || /^\/dashboard\/conference\/registrations\/[^/]+\/edit/.test(String(route?.path || ''))
  if (!onFormEdit) return false
  const path = String(route?.path || '')
  const formId = route?.params?.registrationId
    ?? route?.params?.formId
    ?? path.match(/\/registrations\/([^/]+)\/edit/)?.[1]
    ?? ''
  return isProxyOnlyWorkbench(permissionNames, { formId })
}



/** @deprecated 使用 isProxyOnlyWorkbench */

export function isStoredProxyUser () {

  return isProxyOnlyWorkbench()

}



/** 清除 B 端本地会话（对接人邀请落地后固定个人空间，避免残留 X-Company-Id） */

export function clearProxyGrantSession () {

  localStorage.removeItem('token')

  localStorage.removeItem('user')

  localStorage.removeItem('currentCompanyId')

  localStorage.removeItem(ACL_GRANT_COMPANY_KEY)

  clearWorkspaceScope()

  setCompanyWorkspace()

  clearProxyInviteToken()

}



/**

 * Magic Link / ACL 激活后：固定个人空间（ACL 在 GET /user grants 中，不写 currentCompanyId / 不带 X-Company-Id）

 * 旧调用可传 companyId，已忽略。

 */

export function applyProxyCompanySession () {
  localStorage.removeItem(ACL_GRANT_COMPANY_KEY)

  setCompanyWorkspace()

  return true

}



/** @deprecated 定稿后个人模式不带 X-Company-Id */

export function getAclGrantCompanyId () {

  return null

}



/** @deprecated grants 已写入 localStorage.user，无需单独 sync 租户 id */

export function syncAclTenantContextFromUser () {

  return null

}



/** @deprecated 定稿后个人模式 API 不带 X-Company-Id */

export function resolveRegistrationRequestCompanyId () {

  return null

}



/** @deprecated 纯 ACL 用户无公司切换器 */

export function isAclGrantCompanySession () {

  return false

}



/** 是否仅有 ACL grant、无真实 company_user 成员关系 */

export function isGrantOnlyUser (user) {

  const u = user ?? getStoredUser()

  if (!u) return false

  const companies = u.companies

  const hasMemberCompanies = Array.isArray(companies) && companies.length > 0

  if (hasMemberCompanies) return false

  const grants = getRegistrationFormGrantsFromUser(u)

  const grantPerms = u.resourceGrantPermissionNames ?? u.resource_grant_permission_names

  return grants.length > 0 || (Array.isArray(grantPerms) && grantPerms.length > 0)

}



/**

 * 邀请落地页：已登录用户须先退出，再用自己的账号登录

 * @param {() => Promise<void>} [logoutApi] POST /api/logout

 */

export async function ensureLoggedOutForGrantInvite (logoutApi) {

  if (!localStorage.getItem('token')) return

  try {

    if (typeof logoutApi === 'function') {

      await logoutApi()

    }

  } catch { /* 仍清本地 */ }

  clearProxyGrantSession()

  setCompanyWorkspace()

}



/**

 * 完整 URL 或应用内 path → Vue Router 路径（兼容 /dist/ 前缀）

 */

export function resolveRedirectUrlPath (redirectUrl) {

  const raw = String(redirectUrl || '').trim()

  if (!raw) return null

  try {

    const u = new URL(raw, window.location.origin)

    let path = u.pathname

    const base = (process.env.BASE_URL || '/').replace(/\/$/, '')

    if (base && base !== '/' && path.startsWith(base)) {

      path = path.slice(base.length) || '/'

    }

    if (!path.startsWith('/')) path = `/${path}`

    return `${path}${u.search}${u.hash}`

  } catch {

    if (raw.startsWith('/')) return raw

    return null

  }

}



/** proxyInvite / preview / activate → 跳转 path */

export function resolveGrantRedirectPath (payload) {

  const invite = normalizeProxyInvite(payload) || payload

  return resolveRedirectUrlPath(

    invite?.redirectUrl

    ?? invite?.redirect_url

    ?? invite?.redirectPath

    ?? invite?.redirect_path

  ) || PROXY_DEFAULT_HOME

}



/**

 * 登录成功后跳转（非 invite 场景）

 */

export function resolvePostLoginRedirect (routeQuery = {}, options = {}) {

  const redirect = routeQuery.redirect

  if (typeof redirect === 'string' && redirect.startsWith('/')) {

    return redirect

  }

  if (isProxyEntryBlocked(options.proxyAccess)) {

    return { name: 'ProxyEntryHint' }

  }

  if (hasAlbumCollaboration()) {
    // 照片直播列表即首页 DashboardView（无独立 /dashboard/albums 路由）
    return '/dashboard'
  }

  if (hasProxyRegistrationPermission()) {
    return PROXY_DEFAULT_HOME
  }

  return '/dashboard'

}



/** activate / proxyInvite 写入个人空间并返回跳转 path */

export function applyActivateSession (activated) {

  const payload = activated && typeof activated === 'object' ? activated : {}

  const invite = normalizeProxyInvite(payload) || payload

  applyProxyCompanySession(invite?.companyId ?? payload.companyId)

  return resolveGrantRedirectPath(invite || payload)

}



/**

 * Magic Link 登录成功收尾：个人空间、刷新权限、跳转 redirectUrl

 * @param {import('vue-router').Router} router

 * @param {Record<string, unknown>} authPayload login / phone / wechat 响应

 * @param {{ refreshUserSession?: () => Promise<void>, grantPreview?: object|null }} [options]

 */

export async function finishProxyInviteAuth (router, authPayload = {}, options = {}) {

  const flags = pickGrantAuthFlags(authPayload)

  const invite = flags.proxyInvite

  const preview = options.grantPreview



  applyProxyCompanySession(

    invite?.companyId ?? preview?.companyId ?? null

  )



  if (typeof options.refreshUserSession === 'function') {

    await options.refreshUserSession()

  }



  const redirectPath = resolveGrantRedirectPath(invite || preview || authPayload)

  markInviteOrCollaboratorLinkEntry()

  if (isProxyInviteActivated(authPayload) || isProxyAccessAuthorized(flags.proxyAccess)) {

    clearProxyInviteToken()

  }



  await router.replace(redirectPath)

  return redirectPath

}



/** @deprecated 使用 resolveGrantRedirectPath + router.replace */

export function resolveProxyHomeRoute (activated) {

  const path = resolveGrantRedirectPath(activated)

  if (path.startsWith('/dashboard/conference/registrations')) {

    const highlightId = normalizeProxyInvite(activated)?.registrationId
      || normalizeProxyInvite(activated)?.formId

    return {

      name: 'ConferenceRegistrations',

      query: highlightId ? { highlight: String(highlightId) } : {}

    }

  }

  return path

}

